/**
 * Honest-UI copy (P0-12, platform migration plan Phase 0).
 *
 * Plain strings, no React: the tracking store and the local-notification text
 * use these too, and a store must not import components. Every surface that
 * labels the same limitation imports the same words from here, so they cannot
 * drift apart. When the underlying capability ships, delete the constant and
 * the compiler finds every place that still makes the old disclaimer.
 */

/**
 * The alert triggers and recipients set on a GEOFENCE (enter / exit / dwell,
 * notify roles / users) are not used by anything. Since P1-09 every geofence
 * is also a safety zone, and positions are checked against safety zones that
 * have an alert policy; the geofence's own trigger fields stay inert. See
 * components/safety/ZoneAlertsNotActive.tsx.
 */
export const ZONE_ALERTS_NOT_ACTIVE = 'Alerts not active yet';

/**
 * What a safety zone does right now, as the API reports it
 * (safety_zones alerting_state, P1-09). One label per state, so the roster,
 * the zone panel and the help text cannot disagree.
 */
export type ZoneAlertingState = 'not_evaluated' | 'tenant_disabled' | 'inactive' | 'monitor' | 'no_entry';

export const ZONE_ALERTING_STATE_LABEL: Record<ZoneAlertingState, string> = {
  not_evaluated: 'Reference only',
  tenant_disabled: 'Zone alerts off',
  inactive: 'Not monitored (inactive)',
  monitor: 'Monitored',
  no_entry: 'No-entry alarm',
};

/** A zone's alert policy (safety_zones.zone_policy, migration 543). */
export type ZonePolicy = 'reference' | 'monitor' | 'no_entry';

/**
 * The policy choices offered for one zone. An evaluated policy needs a shape
 * the server can read (CHECK safety_zones_evaluated_has_geog): on an
 * unreadable one only Reference is offered, rather than a choice the server
 * refuses with a 400.
 */
export function zonePolicyChoices(geometryReadable: boolean | undefined): Array<{ value: ZonePolicy; label: string; disabled: boolean }> {
  const unreadable = geometryReadable === false;
  return [
    { value: 'reference', label: 'Reference only', disabled: false },
    { value: 'monitor', label: 'Monitor', disabled: unreadable },
    { value: 'no_entry', label: 'No-entry alarm', disabled: unreadable },
  ];
}

/** One sentence per state, for tooltips and the zone panel. */
export const ZONE_ALERTING_STATE_DETAIL: Record<ZoneAlertingState, string> = {
  not_evaluated: 'Shown on maps only. Positions are not checked against this zone.',
  tenant_disabled:
    'This zone has an alert policy, but zone alerts are turned off for your organization, so positions are not checked.',
  inactive: 'This zone is switched off. Positions are not checked against it.',
  monitor:
    'Positions are checked: entries are recorded and the console is notified. No alarm is paged.',
  no_entry:
    'Entering this zone raises a life-safety alarm that pages until someone owns it.',
};

/**
 * Personnel tracking is foreground-only: no background location on the
 * Android build (commit 3526be7fa) and browsers stop the watch on a hidden tab.
 * See components/safety/ForegroundTrackingNotice.tsx.
 */
export const FOREGROUND_ONLY_TRACKING =
  'Position updates only while Command Bridge is open on screen.';

/** Short form for the compact in-session tracking bar in Field Ops. */
export const FOREGROUND_ONLY_TRACKING_SHORT =
  'Keep the app open: tracking stops in the background.';

/** Title of the ongoing OS notification posted while a session is open. */
export const TRACKING_NOTIFICATION_TITLE = 'Tracking session open';

/**
 * Body of that notification. It is what a responder sees in the notification
 * shade after leaving the app -- exactly when positions have stopped -- so it
 * says so instead of "Recording your position".
 */
export function trackingNotificationBody(callsign?: string | null): string {
  const core = 'Position updates only while Command Bridge is open. Tap to return.';
  return callsign ? `${callsign}: ${core}` : core;
}
